import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DownloadIcon,
  FileIcon,
  FileTextIcon,
  FileVideoIcon,
  FileVolumeIcon,
  PaperclipIcon,
} from 'lucide-react';
import mime from 'mime';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { MODAL_MEDIA_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { Skeleton } from '@/components/ui/skeleton';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { cn } from '@/lib/utils';
import { useAttachment } from '@/modules/humanChat/hooks/useAttachment';
import { pushModal } from '@/redux/modals/actions';
import { Attachment as AttachmentType, AuthorType, ConversationSource } from '@/shared/types/chat';
import { downloadFromUrl, isKeyEnter, noop, preventClickThrough, splitFileName } from '@/util/util';

const floatingCardClasses =
  'rounded-xl bg-white p-1.5 shadow-md transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg';

const fileCardClasses =
  'flex w-60 items-center gap-2 rounded-xl bg-white p-2 shadow-md transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg';

interface Props {
  attachment: AttachmentType;
  source: ConversationSource;
  isSingleItem?: boolean;
  standalone?: boolean;
  fillWidth?: boolean;
  stackCard?: boolean;
  bg?: 'light' | 'dark';
  authorType: `${AuthorType}`;
  handleError?: (error?: Error) => void;
  onImageClick?: (data: {
    file: Buffer | string | null;
    url: string;
    mime_type: string;
    title?: string;
  }) => void;
}

export const Attachment = ({
  attachment,
  source,
  isSingleItem = true,
  standalone = false,
  fillWidth = false,
  stackCard = false,
  bg,
  authorType,
  handleError = noop,
  onImageClick,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showMedia, setShowMedia] = useState(false);
  const attachmentRef = useRef<HTMLDivElement>(null);
  const [renderError, setRenderError] = useState<Error | null>(null);

  const {
    file,
    isLoading,
    error: attachmentError,
    mediaUrl,
  } = useAttachment(source, attachment, authorType, showMedia);

  const isImage = attachment.mime_type?.includes('image') ?? false;
  const isAudio = attachment.mime_type?.includes('audio') ?? false;
  const isVideo = attachment.mime_type?.includes('video') ?? false;
  const isFile = attachment.type === 'file' && !isImage && !isVideo && !isAudio;
  const extension = mime.getExtension(attachment.mime_type);
  // A download with no name is saved as "undefined" with no extension, which then opens in
  // nothing. Fall back to a name built from the mime type so the file stays usable.
  const fallbackName = extension ? `${isVideo ? 'video' : 'file'}.${extension}` : '';
  const downloadName = attachment.title || attachment.filename || fallbackName;
  const handleImageClick = useCallback(() => {
    dispatch(
      pushModal(MODAL_MEDIA_PREVIEW, {
        title: attachment.filename,
        file,
        mime_type: attachment.type === 'file' ? attachment.type : attachment.mime_type,
        url: attachment?.url,
      })
    );
  }, [
    attachment?.filename,
    attachment?.mime_type,
    attachment?.type,
    attachment?.url,
    dispatch,
    file,
  ]);

  useIntersectionObserver({ target: attachmentRef, onIntersect: () => setShowMedia(true) });

  const handleKeyDown = useCallback(
    (e) => {
      preventClickThrough(e);
      if (isKeyEnter(e)) {
        handleImageClick();
      }
    },
    [handleImageClick]
  );

  const handleAttachmentError = () => {
    if (showMedia && mediaUrl) {
      setRenderError(new Error(t('conversation.attachment_error')));
    }
  };
  const error = attachmentError || renderError;
  // useAttachment fills mediaUrl in an effect, so it is empty on the first render even when the
  // attachment carries a url. Read both, or the decision below flips after the effect has already
  // reported the error and MessageGroup has no way to take it back.
  const downloadUrl = mediaUrl || attachment.url;
  // A video that will not play still has a URL behind it, so offer it as a download instead of the
  // dead end below. With no url there is nothing to offer, so that case falls through.
  const showVideoDownload = !!error && isVideo && !!downloadUrl;
  // The integrations query can error while the media fetch is still in flight, and that fetch may
  // yet produce the url the card needs. Hold the report until it settles instead of sending one we
  // cannot take back. The file lands one render before its blob url does, because useAttachment
  // builds that url in an effect, so holding a file still means the url is on its way.
  const videoDownloadPending = !!error && isVideo && (isLoading || (!!file && !downloadUrl));

  useEffect(() => {
    // The download card is a working end state, so it must not bubble up as an error: MessageGroup
    // would put a red line under a healthy card and hide the timestamp.
    if (error && !showVideoDownload && !videoDownloadPending) {
      handleError(error);
    }
  }, [error, handleError, showVideoDownload, videoDownloadPending]);

  const handleFileClick = () => {
    downloadFromUrl(downloadUrl, downloadName);
  };

  if (isLoading) {
    if (isAudio) {
      return <Skeleton className="h-10 w-72 rounded-full" />;
    }
    if (stackCard) {
      return <Skeleton className="h-36 w-36 rounded-lg" />;
    }
    if (standalone) {
      return <Skeleton className={isFile ? 'h-14 w-60 rounded-xl' : 'h-48 w-64 rounded-xl'} />;
    }
    const isSingleFile = attachment.type === 'file' && isSingleItem;
    return <Skeleton className={cn('w-38', isSingleFile ? 'h-5' : 'h-38')} />;
  }
  if (showVideoDownload) {
    const { baseName: videoBaseName, ext: videoExt } = splitFileName(downloadName);
    if (stackCard) {
      return (
        <div ref={attachmentRef}>
          <button
            type="button"
            onClick={handleFileClick}
            className="flex h-36 w-36 cursor-pointer items-center justify-center rounded-lg bg-highlight-background transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.02] hover:shadow-lg"
            aria-label={downloadName || t('common.download')}
          >
            <DownloadIcon size={24} className="text-foreground-muted" />
          </button>
        </div>
      );
    }
    // Grouped attachments are tiled into 150px squares, so the wide card below would be clipped
    // mid-name. Take the shape of the neighbouring tiles instead: icon over extension, centered.
    if (!isSingleItem) {
      return (
        <div ref={attachmentRef}>
          <button
            type="button"
            onClick={handleFileClick}
            className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 bg-background"
            aria-label={downloadName || t('common.download')}
          >
            <DownloadIcon size={40} className="text-foreground-muted" />
            <span className="text-sm leading-5 font-extrabold text-foreground">{videoExt}</span>
          </button>
        </div>
      );
    }
    return (
      <div ref={attachmentRef}>
        <button
          type="button"
          onClick={handleFileClick}
          className={cn(fileCardClasses, 'cursor-pointer')}
          aria-label={downloadName || t('common.download')}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-highlight-background">
            <DownloadIcon size={20} className="text-foreground-muted" />
          </div>
          <div className="flex min-w-0 flex-col overflow-hidden text-left">
            <span className="truncate text-sm leading-5 font-medium text-foreground">
              {videoBaseName || t('common.download')}
            </span>
            <span className="text-xs leading-4 font-normal text-foreground-muted uppercase">
              {videoExt}
            </span>
          </div>
        </button>
      </div>
    );
  }

  if (error && (isImage || isVideo)) {
    return (
      <div className="cursor-not-allowed [&>*]:pointer-events-none [&>*:not(img)]:opacity-50">
        <img className="w-50" src="/assets/broken_image.png" alt={t('common.fallback_image')} />
      </div>
    );
  }

  if (stackCard && isImage) {
    const stackClickHandler = onImageClick
      ? () =>
          onImageClick({
            file,
            url: mediaUrl ?? attachment.url,
            mime_type: attachment.mime_type,
            title: attachment.filename,
          })
      : handleImageClick;
    return (
      <div ref={attachmentRef}>
        <button
          type="button"
          className={cn(
            'block rounded-xl',
            error ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          )}
          onClick={error ? undefined : stackClickHandler}
          aria-label={attachment.filename || t('common.fallback_image')}
        >
          <img
            src={mediaUrl}
            alt={attachment.filename || t('common.fallback_image')}
            className="block max-h-48 max-w-48 rounded-lg"
            onError={handleAttachmentError}
          />
        </button>
      </div>
    );
  }

  if (stackCard && isVideo) {
    return (
      <div ref={attachmentRef}>
        <video
          src={mediaUrl}
          controls
          className="block max-h-48 max-w-48 rounded-lg"
          onError={handleAttachmentError}
        />
      </div>
    );
  }

  if (standalone && isImage) {
    const hasError = !!error;
    return (
      <div ref={attachmentRef}>
        <button
          type="button"
          className={cn(
            floatingCardClasses,
            fillWidth ? 'block w-full' : 'inline-block max-w-full',
            hasError ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          )}
          onClick={hasError ? undefined : handleImageClick}
          aria-label={attachment.filename || t('common.fallback_image')}
        >
          <img
            src={mediaUrl}
            alt={attachment.filename || t('common.fallback_image')}
            className={cn(
              'block max-h-72 rounded-lg object-contain',
              fillWidth ? 'h-auto w-full' : 'w-auto max-w-72'
            )}
            onError={handleAttachmentError}
          />
        </button>
      </div>
    );
  }

  if (standalone && isVideo) {
    const hasError = !!error;
    return (
      <div ref={attachmentRef}>
        <div className={cn(floatingCardClasses, hasError && 'cursor-not-allowed opacity-50')}>
          <video
            src={mediaUrl}
            controls
            className={cn(
              'block max-h-72 rounded-lg',
              fillWidth ? 'h-auto w-full' : 'w-auto max-w-72'
            )}
            onError={handleAttachmentError}
          />
        </div>
      </div>
    );
  }

  if (standalone && isFile) {
    const { baseName: fileBaseName, ext: fileExt } = splitFileName(
      attachment.title || attachment.filename || ''
    );
    return (
      <div ref={attachmentRef}>
        <button
          type="button"
          onClick={error ? undefined : handleFileClick}
          className={cn(
            fileCardClasses,
            error ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          )}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-highlight-background">
            <FileTextIcon size={20} className="text-foreground-muted" />
          </div>
          <div className="flex min-w-0 flex-col overflow-hidden text-left">
            <span className="truncate text-sm leading-5 font-medium text-foreground">
              {fileBaseName}
            </span>
            <span className="text-xs leading-4 font-normal text-foreground-muted uppercase">
              {fileExt}
            </span>
          </div>
        </button>
      </div>
    );
  }

  if (isSingleItem) {
    const hasError = !!error;
    return (
      <div
        ref={attachmentRef}
        className={cn(
          '[&_video]:max-w-full [&:not(.has-error)_img]:cursor-pointer',
          isImage && 'h-75 w-75 overflow-hidden',
          isAudio && 'w-75',
          hasError &&
            'has-error cursor-not-allowed [&>*]:pointer-events-none [&>*:not(img)]:opacity-50'
        )}
      >
        {isImage && (
          <img
            src={mediaUrl}
            alt="Attachment"
            onError={handleAttachmentError}
            onClick={handleImageClick}
            onKeyDown={handleKeyDown}
            role="presentation"
          />
        )}
        {isAudio && <audio src={mediaUrl} controls onError={handleAttachmentError} />}
        {isVideo && <video src={mediaUrl} controls onError={handleAttachmentError} />}
        {isFile && (
          <button
            className={cn(
              'flex items-center gap-1 bg-transparent text-informational hover:underline',
              bg === 'dark' && 'text-text-primary-foreground'
            )}
            onClick={error ? undefined : handleFileClick}
          >
            <PaperclipIcon
              size={16}
              className={bg === 'dark' ? 'text-background' : 'text-foreground-muted'}
            />
            {attachment.title || attachment.filename}
          </button>
        )}
      </div>
    );
  }

  const hasError = !!(renderError || error);
  return (
    <div
      ref={attachmentRef}
      className={cn(
        'flex max-w-75 items-center [&_video]:w-full [&:not(.has-error)_img]:cursor-pointer',
        hasError &&
          'has-error cursor-not-allowed [&>*]:pointer-events-none [&>*:not(img)]:opacity-50'
      )}
    >
      {isImage && (
        <img
          src={mediaUrl}
          alt="Attachment"
          onError={handleAttachmentError}
          onClick={handleImageClick}
          onKeyDown={handleKeyDown}
          role="presentation"
        />
      )}
      {isAudio && (
        <button
          className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 bg-background text-informational"
          onClick={handleImageClick}
        >
          <FileVolumeIcon size={40} className="text-foreground-muted" />
          <span className="text-sm leading-5 font-extrabold text-foreground">{extension}</span>
        </button>
      )}
      {isVideo && (
        <button
          className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 bg-background text-informational"
          onClick={handleImageClick}
        >
          <FileVideoIcon size={40} className="text-foreground-muted" />
          <span className="text-sm leading-5 font-extrabold text-foreground">{extension}</span>
        </button>
      )}
      {isFile && (
        <button
          className="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 bg-background text-informational"
          onClick={handleFileClick}
        >
          <FileIcon size={15} className="text-foreground-muted" />
          <span className="text-sm leading-5 font-semibold text-foreground">{extension}</span>
        </button>
      )}
    </div>
  );
};
